import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material'
import { GreenText } from 'components'
import { CompanyDto, DialogProps } from 'dtos'
import { VehicleClusterInformationDto, vehicleClusterInformationValidationSchema } from 'dtos/vehicleClusterInformationDto'
import { Form, Formik } from 'formik'
import { useLoadingState } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import { useState } from 'react'
import { getCompaniesAutocompleteBase, useDebounce } from 'utils'

export default function ClusterInformationAddEditDialog({
  open,
  onClose,
  onSave,
  initialValues,
  isLoading
}: DialogProps<VehicleClusterInformationDto>) {
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingClusterConversionCompanies: false,
    isGettingClusterProgrammingCompanies: false
  })

  const [clusterConversionCompanies, setClusterConversionCompanies] = useState<CompanyDto[]>([])
  const [clusterProgrammingCompanies, setClusterProgrammingCompanies] = useState<CompanyDto[]>([])

  const getClusterConversionCompaniesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingClusterConversionCompanies', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'isClusterConversionCompany'])
      .then(response => {
        setClusterConversionCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingClusterConversionCompanies', false)
      })
  }, DEBOUNCE_DELAY)

  const getClusterProgrammingCompaniesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingClusterProgrammingCompanies', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'isClusterProgrammerCompany'])
      .then(response => {
        setClusterProgrammingCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingClusterProgrammingCompanies', false)
      })
  }, DEBOUNCE_DELAY)

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={values => {
          onSave(values)
        }}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={vehicleClusterInformationValidationSchema}
      >
        {({ errors, handleBlur, handleChange, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <Form>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Edit Cluster Information</DialogTitle>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || isSubmitting}
                      error={Boolean(
                        touched.vehicleCheckInOdometerReadingMileage && errors.vehicleCheckInOdometerReadingMileage
                      )}
                      helperText={touched.vehicleCheckInOdometerReadingMileage && errors.vehicleCheckInOdometerReadingMileage}
                      label='Vehicle Check-In Odometer Reading'
                      name='vehicleCheckInOdometerReadingMileage'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type='number'
                      value={values.vehicleCheckInOdometerReadingMileage}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl>
                      <RadioGroup
                        name='vehicleCheckInOdometerReadingUnitOfMeasurement'
                        onBlur={handleBlur}
                        onChange={handleChange}
                        row
                        value={values.vehicleCheckInOdometerReadingUnitOfMeasurement}
                      >
                        <FormControlLabel
                          control={<Radio />}
                          disabled={isLoading || isSubmitting}
                          label='Miles'
                          value='Miles'
                        />
                        <FormControlLabel
                          control={<Radio />}
                          disabled={isLoading || isSubmitting}
                          label='Kilometers'
                          value='Kilometers'
                        />
                      </RadioGroup>

                      {Boolean(
                        touched.vehicleCheckInOdometerReadingUnitOfMeasurement &&
                          errors.vehicleCheckInOdometerReadingUnitOfMeasurement
                      ) && (
                        <FormHelperText error>
                          {touched.vehicleCheckInOdometerReadingUnitOfMeasurement &&
                            errors.vehicleCheckInOdometerReadingUnitOfMeasurement}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl>
                      <FormLabel error={Boolean(touched.convertCluster && errors.convertCluster)}>Convert Cluster</FormLabel>

                      <RadioGroup
                        name='convertCluster'
                        onChange={e => {
                          setFieldValue('convertCluster', e.target.value === 'true')
                        }}
                        row
                        value={values.convertCluster}
                      >
                        <FormControlLabel disabled={isLoading || isSubmitting} value='true' control={<Radio />} label='Yes' />
                        <FormControlLabel disabled={isLoading || isSubmitting} value='false' control={<Radio />} label='No' />
                      </RadioGroup>

                      {Boolean(/*touched.convertCluster &&*/ errors.convertCluster) && (
                        <FormHelperText error>{/*touched.convertCluster &&*/ errors.convertCluster}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl>
                      <FormLabel
                        error={Boolean(
                          touched.clusterConversionInHouseOrThirdParty && errors.clusterConversionInHouseOrThirdParty
                        )}
                      >
                        Cluster Conversion In-House Or 3rd Party
                      </FormLabel>

                      <RadioGroup
                        name='clusterConversionInHouseOrThirdParty'
                        onBlur={handleBlur}
                        onChange={e => {
                          setFieldValue('clusterConversionInHouseOrThirdParty', e.target.value)

                          if (e.target.value === 'In-House') {
                            setFieldValue('thirdPartyClusterConversionCompany', undefined)
                          }
                        }}
                        row
                        value={values.clusterConversionInHouseOrThirdParty}
                      >
                        <FormControlLabel
                          control={<Radio />}
                          disabled={isLoading || isSubmitting}
                          label='In-House'
                          value='In-House'
                        />
                        <FormControlLabel
                          control={<Radio />}
                          disabled={isLoading || isSubmitting}
                          label='3rd Party'
                          value='3rd Party'
                        />
                      </RadioGroup>

                      {Boolean(touched.clusterConversionInHouseOrThirdParty && errors.clusterConversionInHouseOrThirdParty) && (
                        <FormHelperText error>
                          {touched.clusterConversionInHouseOrThirdParty && errors.clusterConversionInHouseOrThirdParty}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || values.clusterConversionInHouseOrThirdParty !== '3rd Party'}
                      disableListWrap
                      getOptionLabel={thirdPartyClusterConversionCompany => thirdPartyClusterConversionCompany.name!}
                      noOptionsText={
                        loadingState.isGettingClusterConversionCompanies
                          ? 'Searching Cluster Conversion Companies...'
                          : 'No Cluster Conversion Companies. Start typing to search for Cluster Conversion Companies.'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('thirdPartyClusterConversionCompany', value || undefined)
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getClusterConversionCompaniesAutocomplete(value)
                      }}
                      options={clusterConversionCompanies.map(thirdPartyClusterConversionCompany => ({
                        companyGuid: thirdPartyClusterConversionCompany.companyGuid,
                        name: thirdPartyClusterConversionCompany.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(
                            touched.thirdPartyClusterConversionCompany && errors.thirdPartyClusterConversionCompany
                          )}
                          helperText={touched.thirdPartyClusterConversionCompany && errors.thirdPartyClusterConversionCompany}
                          label='3rd Party Cluster Conversion Company'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingClusterConversionCompanies && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      value={
                        values.thirdPartyClusterConversionCompany
                          ? {
                              companyGuid: values.thirdPartyClusterConversionCompany.companyGuid,
                              name: values.thirdPartyClusterConversionCompany.name
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl>
                      <FormLabel error={Boolean(touched.additionalProgramming && errors.additionalProgramming)}>
                        Additional Programming
                      </FormLabel>

                      <RadioGroup
                        name='additionalProgramming'
                        onBlur={handleBlur}
                        onChange={e => {
                          const additionalProgramming = e.target.value === 'true'

                          setFieldValue('additionalProgramming', additionalProgramming)

                          if (additionalProgramming === false) {
                            setFieldValue('additionalProgrammingInHouseOrThirdParty', undefined)
                            setFieldValue('thirdPartyAdditionalProgrammingCompany', undefined)
                          }
                        }}
                        row
                        value={values.additionalProgramming}
                      >
                        <FormControlLabel control={<Radio />} disabled={isLoading || isSubmitting} label='Yes' value='true' />
                        <FormControlLabel control={<Radio />} disabled={isLoading || isSubmitting} label='No' value='false' />
                      </RadioGroup>

                      {Boolean(touched.additionalProgramming && errors.additionalProgramming) && (
                        <FormHelperText error>{touched.additionalProgramming && errors.additionalProgramming}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl>
                      <FormLabel>Additional Programming In-House or 3rd Party</FormLabel>

                      <RadioGroup
                        name='additionalProgrammingInHouseOrThirdParty'
                        onBlur={handleChange}
                        onChange={e => {
                          setFieldValue('additionalProgrammingInHouseOrThirdParty', e.target.value)

                          if (e.target.value === 'In-House') {
                            setFieldValue('thirdPartyAdditionalProgrammingCompany', undefined)
                          }
                        }}
                        row
                        value={values.additionalProgrammingInHouseOrThirdParty}
                      >
                        <FormControlLabel
                          control={<Radio />}
                          disabled={isLoading || isSubmitting || values.additionalProgramming !== true}
                          label='In-House'
                          value='In-House'
                        />

                        <FormControlLabel
                          control={<Radio />}
                          disabled={isLoading || isSubmitting || values.additionalProgramming !== true}
                          label='3rd Party'
                          value='3rd Party'
                        />
                      </RadioGroup>

                      {Boolean(
                        touched.additionalProgrammingInHouseOrThirdParty && errors.additionalProgrammingInHouseOrThirdParty
                      ) && (
                        <FormHelperText error>
                          {touched.additionalProgrammingInHouseOrThirdParty && errors.additionalProgrammingInHouseOrThirdParty}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || values.additionalProgrammingInHouseOrThirdParty !== '3rd Party'}
                      disableListWrap
                      getOptionLabel={thirdPartyAdditionalProgrammingCompany => thirdPartyAdditionalProgrammingCompany.name!}
                      noOptionsText={
                        loadingState.isGettingClusterProgrammingCompanies
                          ? 'Searching for Cluster Programming Companies...'
                          : 'No Cluster Programming Companies. Start typing to search for Cluster Programming Companies.'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('thirdPartyAdditionalProgrammingCompany', value || undefined)
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getClusterProgrammingCompaniesAutocomplete(value)
                      }}
                      options={clusterProgrammingCompanies.map(thirdPartyAdditionalProgrammingCompany => ({
                        companyGuid: thirdPartyAdditionalProgrammingCompany.companyGuid,
                        name: thirdPartyAdditionalProgrammingCompany.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(
                            touched.thirdPartyAdditionalProgrammingCompany && errors.thirdPartyAdditionalProgrammingCompany
                          )}
                          helperText={
                            touched.thirdPartyAdditionalProgrammingCompany && errors.thirdPartyAdditionalProgrammingCompany
                          }
                          label='3rd Party Additional Programming Company'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingClusterProgrammingCompanies && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      value={
                        values.thirdPartyAdditionalProgrammingCompany
                          ? {
                              companyGuid: values.thirdPartyAdditionalProgrammingCompany.companyGuid,
                              name: values.thirdPartyAdditionalProgrammingCompany.name
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || isSubmitting}
                      error={Boolean(
                        touched.clusterReinstallationOdometerReadingMi && errors.clusterReinstallationOdometerReadingMi
                      )}
                      helperText={
                        touched.clusterReinstallationOdometerReadingMi && errors.clusterReinstallationOdometerReadingMi
                      }
                      label='Current Odometer Reading (After Cluster Reinstallation Complete)'
                      name='clusterReinstallationOdometerReadingMi'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type='number'
                      value={values.clusterReinstallationOdometerReadingMi}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>**Coming soon!**</GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={isLoading || isSubmitting} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton loading={isLoading || isSubmitting} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
